import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaCar, 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaArrowRight,
  FaCheck,
  FaClock,
  FaShieldAlt,
  FaGasPump,
  FaUserTie,
  FaUsers,
  FaSuitcase,
  FaSnowflake,
  FaMusic
} from 'react-icons/fa';

const CarRental = () => {
  const [formData, setFormData] = useState({
    rentalType: 'self-drive',
    pickupLocation: '',
    pickupDate: '',
    pickupTime: '',
    returnDate: '',
    returnTime: '',
    carCategory: '',
    driverAge: '',
    specialRequests: '',
    fullName: '',
    email: '',
    phone: '',
    licenseNumber: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const rentalTypes = [
    {
      value: 'self-drive',
      label: 'Self Drive',
      description: 'Drive yourself with full freedom',
      icon: FaCar
    },
    {
      value: 'chauffeur',
      label: 'With Chauffeur',
      description: 'Professional driver included',
      icon: FaUserTie
    }
  ];

  const pickupLocations = [
    'Lagos - Murtala Muhammed Airport',
    'Lagos - Victoria Island',
    'Lagos - Ikeja',
    'Lagos - Lekki',
    'Abuja - Nnamdi Azikiwe Airport',
    'Abuja - Central Area',
    'Abuja - Garki',
    'Abuja - Wuse',
  ];

  const carCategories = [
    {
      category: 'economy',
      name: 'Economy',
      description: 'Fuel-efficient compact cars',
      features: ['4 seats', '2 bags', 'Manual transmission', 'A/C'],
      examples: 'Toyota Corolla, Nissan Sunny',
      icon: FaCar
    },
    {
      category: 'standard',
      name: 'Standard',
      description: 'Comfortable mid-size vehicles',
      features: ['5 seats', '3 bags', 'Automatic', 'A/C'],
      examples: 'Toyota Camry, Honda Accord',
      icon: FaCar
    },
    {
      category: 'suv',
      name: 'SUV',
      description: 'Spacious for families and groups',
      features: ['7 seats', '5 bags', 'Automatic', 'A/C'],
      examples: 'Land Cruiser Prado, Lexus Gx460,',
      icon: FaCar
    },
    {
      category: 'luxury',
      name: 'Luxury',
      description: 'Premium vehicles with top features',
      features: ['5 seats', '4 bags', 'Automatic', 'Premium features'],
      examples: 'Mercedes Maybach, Cadillac Escalade',
      icon: FaCar
    }
  ];

  const ageRanges = [
    { value: '18-25', label: '18-25 years' },
    { value: '26-35', label: '26-35 years' },
    { value: '36-50', label: '36-50 years' },
    { value: '50+', label: '50+ years' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const formDataToSend = {
      ...formData,
      _captcha: false,
      _template: "table",
      _autoresponse: "Thanks for your request! We'll respond shortly.",
      _token: "10d0b5f657f51e47f0b98f7f43832928", // Replace with your actual token
    };
  
    const encoded = new URLSearchParams(formDataToSend).toString();
  
    try {
      await fetch("https://formsubmit.co/10d0b5f657f51e47f0b98f7f43832928", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: encoded,
      });
  
      // Show success message in UI
      setIsSubmitted(true);
  
      // Optionally clear the form
      // setFormData(initialState);
  
      // Hide after a few seconds if needed
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error("FormSubmit failed:", error);
      alert("There was a problem submitting the form.");
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 gradient-green overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/3024008/pexels-photo-3024008.jpeg')`
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <FaCar className="text-3xl" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold">Car Rental</h1>
            </div>
            <p className="text-xl md:text-2xl text-green-100 max-w-3xl mx-auto leading-relaxed">
              Rent a car for ultimate freedom and convenience. Choose from economy to luxury vehicles, 
              with or without a professional chauffeur, across major Nigerian cities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-12 shadow-2xl text-center max-w-2xl mx-auto"
            >
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
                <FaCheck className="text-white text-3xl" />
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-6">Car Rental Request Submitted!</h3>
              <p className="text-lg text-gray-600 mb-4">
                Thank you for choosing Yowa Travels for your car rental needs.
              </p>
              <p className="text-green-600 font-semibold text-lg">
                An agent will be with you shortly to confirm availability and provide rental details!
              </p>
              <div className="mt-8 p-6 bg-green-50 rounded-2xl">
                <p className="text-sm text-green-700">
                  <strong>What's Next?</strong><br />
                  We'll send you available vehicle options, pricing, and pickup instructions within an hour.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <form onSubmit={handleSubmit} className="p-8">
                {/* Rental Type Selection */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Rental Type</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {rentalTypes.map((type) => {
                      const IconComponent = type.icon;
                      return (
                        <label
                          key={type.value}
                          className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-300 ${
                            formData.rentalType === type.value
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-green-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="rentalType"
                            value={type.value}
                            checked={formData.rentalType === type.value}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className="text-center">
                            <IconComponent className="text-green-500 text-3xl mx-auto mb-4" />
                            <h5 className="font-bold text-gray-800 mb-2">{type.label}</h5>
                            <p className="text-sm text-gray-600">{type.description}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Pickup Location */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaMapMarkerAlt className="text-green-500" />
                    <span>Pickup Location</span>
                  </h4>
                  
                  <div>
                    <label className="form-label">Pickup Location *</label>
                    <select
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    >
                      <option value="">Select pickup location</option>
                      {pickupLocations.map((location, index) => (
                        <option key={index} value={location}>
                          {location}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Rental Dates */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6 flex items-center space-x-2">
                    <FaCalendarAlt className="text-green-500" />
                    <span>Rental Period</span>
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Pickup Date *</label>
                      <input
                        type="date"
                        name="pickupDate"
                        value={formData.pickupDate}
                        onChange={handleInputChange}
                        className="form-input"
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Pickup Time *</label>
                      <input
                        type="time"
                        name="pickupTime"
                        value={formData.pickupTime}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Return Date *</label>
                      <input
                        type="date"
                        name="returnDate"
                        value={formData.returnDate}
                        onChange={handleInputChange}
                        className="form-input"
                        min={formData.pickupDate || new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Return Time *</label>
                      <input
                        type="time"
                        name="returnTime"
                        value={formData.returnTime}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Car Category */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Choose Your Vehicle</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {carCategories.map((car) => {
                      const IconComponent = car.icon;
                      return (
                        <label
                          key={car.category}
                          className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-300 ${
                            formData.carCategory === car.category
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-green-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="carCategory"
                            value={car.category}
                            checked={formData.carCategory === car.category}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div>
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <h5 className="font-bold text-gray-800 text-lg mb-1">{car.name}</h5>
                                <p className="text-gray-600 text-sm mb-2">{car.description}</p>
                                <p className="text-sm text-gray-500 mb-3">{car.examples}</p>
                              </div>
                      
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {car.features.map((feature, index) => (
                                <div key={index} className="flex items-center space-x-2">
                                  <FaCheck className="text-green-500 text-sm" />
                                  <span className="text-gray-600 text-sm">{feature}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Driver Age */}
                {formData.rentalType === 'self-drive' && (
                  <div className="mb-8">
                    <h4 className="text-xl font-bold text-gray-800 mb-6">Driver Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="form-label">Driver's Age Range *</label>
                        <select
                          name="driverAge"
                          value={formData.driverAge}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        >
                          <option value="">Select age range</option>
                          {ageRanges.map((age) => (
                            <option key={age.value} value={age.value}>
                              {age.label}
                            </option>
                          ))}
                        </select>
                        <p className="text-sm text-gray-500 mt-2">Minimum age requirement: 21 years</p>
                      </div>
                      <div>
                        <label className="form-label">Driving License Number *</label>
                        <input
                          type="text"
                          name="licenseNumber"
                          value={formData.licenseNumber}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Enter license number"
                          required
                        />
                        <p className="text-sm text-gray-500 mt-2">Valid Nigerian or International license required</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Special Requests */}
                <div className="mb-8">
                  <label className="form-label">Special Requests (Optional)</label>
                  <textarea
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    rows="4"
                    className="form-input resize-none"
                    placeholder="GPS navigation, child car seat, toll pass, specific color preference, additional driver, or any other special requirements..."
                  ></textarea>
                </div>

                {/* Contact Information */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter email address"
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="+234 (0) 900 000 0000"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <button
                    type="submit"
                    className="btn-yowa-primary text-lg px-12 py-4 inline-flex items-center space-x-3"
                  >
                    <FaCar />
                    <span>Request Car Rental</span>
                    <FaArrowRight />
                  </button>
                  <p className="text-sm text-gray-500 mt-4">
                    No upfront payment. We'll confirm availability and send rental agreement details.
                  </p>
                </div>
              </form>
            </motion.div>
          )}
        </div>
      </section>

      {/* Why Choose Our Car Rental */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Why Rent with Yowa Travels?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enjoy peace of mind with our well-maintained fleet, comprehensive insurance, and 24/7 roadside assistance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: FaShieldAlt,
                title: 'Comprehensive Insurance',
                description: 'All vehicles come with full coverage insurance and 24/7 roadside assistance for your peace of mind.',
                color: 'bg-green-500'
              },
              {
                icon: FaGasPump,
                title: 'Fair Fuel Policy',
                description: 'Pick up with a full tank, return with any level. We only charge for the fuel you actually use.',
                color: 'bg-blue-500'
              },
              {
                icon: FaClock,
                title: 'Flexible Terms',
                description: 'Short-term rentals, extended periods, or long-term leases. We accommodate all your needs.',
                color: 'bg-purple-500'
              }
            ].map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className={`w-16 h-16 ${benefit.color} rounded-full flex items-center justify-center mx-auto mb-6`}>
                    <IconComponent className="text-white text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CarRental;